import moment from 'moment';
import axios from 'axios';

import clientsService from '../clientsService/clientsService';
import idConstants from '../../config/idConstants';

const _calculateTimes = (severity, inspectionDate) => {
  const numMonths = moment(Date.now()).diff(moment(inspectionDate), 'months');
  let timeWeight = 0;
  if (numMonths < 7) {
    timeWeight = 3;
  } else if (numMonths < 13) {
    timeWeight = 2;
  } else if (numMonths < 25) {
    timeWeight = 1;
  }
  const timeSeverity = severity * timeWeight;
  const pointsExpire = moment(inspectionDate).add(2, 'years').format('YYYY-MM-DD');
  return {timeWeight, timeSeverity, pointsExpire}
};

const getCrashLocationReport = (clientId, forUser, sites, reportName, signal = null) => {
  return new Promise((resolve, reject) => {
    clientsService.getCrashReportData(clientId, forUser, sites, reportName, signal)
    .then(results => {
      const resultList = results.map(item => ({
        clientName: item.Name,
        crashDate: item[''] ? moment(item['']) : null,
        state: item.RepState,
        lastName: item.LastName,
        firstName: item.FirstName,
        reportNum: item.RepNum,
        hazmat: item.IsHazmat,
        injuries: item.Injuries,
        fatalities: item.Fatalities,
        towed: item.IsTowAway,
        employeeId: item.EMID,
        legacyLocationId: item.ERID,
        locationMongoId: item.mongoId,
        points: item.AdjCraPts
      }));
      resolve(resultList);
    })
    .catch(reject);
  });
};

const getCrashDOTReport = (clientId, forUser, dotNums, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    clientsService.getCrashReportData(clientId, forUser, dotNums, reportType, signal)
    .then(results => {
      const resultList = results.map((item) => {
        return {
          clientName: item.clientName,
          crashDate: item.crashDate ? moment(item.crashDate) : null,
          state: item.repState,
          lastName: item.lastName,
          firstName: item.firstName,
          reportNum: item.repNum,
          hazmat: item.hazmat,
          injuries: item.injuries,
          fatalities: item.fatalities,
          towed: item.towAway,
          employeeId: item.employeeId,
          legacyLocationId: item.legacyClientId,
          locationMongoId: item.mongoId,
          points: item.points,
          dotNum: item.dotNum
        }
      });
      resolve(resultList);
    })
    .catch(reject);
  });
};
const getEmployeesDOTReport = (clientId, forUser, dotNums, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    clientsService.getEmployeesDOTReportData(clientId, forUser, dotNums, reportType, signal)
    .then(resolve)
    .catch(reject);
  });
};

const getCSALocationReport = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    clientsService.getCSAReportData(clientId, forUser, sites, reportType, signal)
    .then(results => {
      const resultList = results.map(item => ({...item, ..._calculateTimes(item.severity, item.inspectionDate)}));
      resolve(resultList);
    })
    .catch(reject);
  });
};

const getCSADOTReport = (clientId, forUser, dotNums, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    clientsService.getCSAReportData(clientId, forUser, dotNums, reportType, signal)
    .then(results => {
      const resultList = results.map(item => ({...item, ..._calculateTimes(item.severity, item.inspectionDate)}));
      resolve(resultList);
    })
    .catch(reject);
  });
};

const getCSABasicsDOTScoreGraph = (dotNumber, signal = null) => {
  return new Promise((resolve, reject) => {
    axios
    .get(`/v1/clients/0/csaBasics?dotNumber=${dotNumber}`, { signal })
    .then(res => {
      if (res.status !== 200) {
        return reject(new Error('Unexpected response from server'));
      }
      resolve(res.data);
    })
    .catch(reject);
  });
};

const getDocumentReport = (
    clientId,
    sites,
    applicant,
    employee,
    inactive,
    terminated,
    missing,
    expired,
    expiredDays,
    expiring,
    expiringDays,
    signal = null
) => {
  return new Promise((resolve, reject) => {
    clientsService.getMissingAndExpiredDocuments(clientId, sites, applicant, employee, inactive, terminated, missing, expired, expiredDays, expiring, expiringDays, signal)
    .then(response => {
      // Get today's date to use it for comparisons
      const today = new moment().startOf('day');
      // If we want expired docs, but expired for more than 0 days, calculate the date
      const alertList = (response || []).reduce((alertList, docAlert) => {
        const docDueDate = new moment(docAlert.docDueDate);
        const isExpiredNow = docDueDate.isBefore(today);
        // Set the status
        if (!docAlert.docImageId) {
          docAlert.itemStatus = 'Missing';
          docAlert.docDueDate = null;
        } else if (docAlert.itemStatus === 'Compliant' || docAlert.itemStatus === 'Missing') {
          docAlert.itemStatus = isExpiredNow ? 'Expired' : 'Expiring';
        }
        // Add it to the alertList that we'll display in the report
        alertList.push(docAlert);
        return alertList;
      }, []);
      resolve(alertList);
    })
    .catch(reject);
  });
};

const getComplianceReport = (clientId, sites, expiringDays, signal) => {
  return new Promise((resolve, reject) => {
    clientsService.getMissingDocuments(clientId, sites, true, signal)
    .then(documentList => {
      const alertList = (documentList || []).reduce((alertList, docAlert) => {
        if ((docAlert.itemStatus === 'Compliant' || docAlert.itemStatus === 'Missing') && docAlert.docImageId) {
          docAlert.itemStatus = 'Expired';
        } else if (docAlert.itemStatus === 'Compliant') {
          docAlert.itemStatus = 'Missing';
          docAlert.docDueDate = null;
        }
        alertList.push(docAlert);
        return alertList;
      }, []);
      return resolve(alertList.filter(document => idConstants.complianceDocTypes.some((docType) =>
          document.docName.toLowerCase() === docType
      )));
    })
    .catch(reject);
  });
};

const getAccidentLocationReport = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    clientsService.getAccidentReportData(clientId, forUser, sites, reportType, signal)
    .then(results => {
      const resultList = results.map((item) => {
        return {
          clientName: item.Name,
          city: item.City,
          accidentDate: item.AccidentDate ? moment(item.AccidentDate) : null,
          state: item.State,
          lastName: item.LName,
          firstName: item.FName,
          cited: item.Cited,
          hazmat: item.HazMat,
          injuries: item.Injuries,
          fatalities: item.Fatalities,
          disabled: item.VehDisabled,
          employeeId: item.EMID,
          legacyLocationId: item.ERID,
          locationMongoId: item.mongoId
        }
      });
      resolve(resultList);
    })
    .catch(reject);
  });
};

// this is calling the same thing as something else?
const getAccidentDOTReport = (clientId, forUser, sites, reportType, signal = null) => {
  return new Promise((resolve, reject) => {
    clientsService.getAccidentReportData(clientId, forUser, sites, reportType, signal)
    .then(results => {
      const resultList = results.map((item) => {
        return {
          clientName: item.name,
          city: item.city,
          accidentDate: item.accidentDate ? moment(item.accidentDate) : null,
          state: item.state,
          lastName: item.lastName,
          firstName: item.firstName,
          cited: item.cited,
          hazmat: item.hazmat,
          injuries: item.injuries,
          fatalities: item.fatalities,
          disabled: item.disabled,
          employeeId: item.EMID,
          legacyLocationId: item.ERID,
          dotNum: item.dotNum
        }
      });
      resolve(resultList);
    })
    .catch(reject);
  });
};

const getReportOfAllMedCardsByLocation = async ({clientId, isIncludeChildren, signal = null}) => {
  try {
    const results = await clientsService.getReportOfAllMedCardsByLocation({
      clientId, 
      isIncludeChildren, 
      signal
    });
    const resultList = results.map((item) => {
      return {
        companyName: item.companyName,
        locationName: item.locationName,
        employeeName: item.employeeName,
        ssn: item.ssn,
        workId: item.workID, 
        documentBucketName: item.documentBucketName,
        docDate: item.docDate ? moment(item.docDate).toISOString() : null,
        expirationDate: item.expirationDate ? moment(item.expirationDate).toISOString() : null,
        EMID: item.emid
      };
    });
    return resultList;
  } catch (err) {
    console.error('Error fetching med cards:', err);
    throw err;
  }
};

/**
 * Gathers the FMCSA report data for the given DOT number and year.
 * @param {Object} params - The parameters for the report.
 * @param {string} params.dotNumber - The DOT number.
 * @param {number} params.year - The year for the report.
 * @param {number} params.testingAuthorityId - The ID of the testing authority. 
 * @param {object} params.signal - AbortController signal to cancel the request (optional).
 * @returns {Promise<Object>} - A promise that resolves to the FMCSA report data.
 */
const getMisReport = async ({ 
  dotNumber, 
  year, 
  testingAuthorityId,
  signal = null 
}) => {
  try {
    const response = await axios.get('/v1/drugTesting/fasttest/managementInformationSystemsReports', {
      params: {
        dotNumber,
        year,
        testingAuthorityId
      },
      signal
    });
    const misReport = response.data;

    return misReport;
  } catch (err) {
    console.error('Error fetching MIS report:', err);
    throw err;
  }
};

/**
 * Gets the blank MIS report form from the server.
 * @param {Object} signal - AbortController signal to cancel the request (optional).
 * @returns {Promise<string>} - A promise that resolves to the binary data of the blank MIS report form.
 * @throws {Error} - Throws an error if the response is not valid or if the request fails.
 */
const getBlankMisReportForm = async (signal = null) => {
  try {
    const response = await axios.get('/v1/settings/documents/BlankMisReportForm', { signal });
    
    if (response.status !== 200 || !response.data || !response.data.binaryData) {
      throw new Error('Unexpected response from server');
    }
    if (!response.data.binaryData.data || !response.data.binaryData.data.length) {
      throw new Error('Received empty PDF buffer');
    }

    const misReportBufferArray = response.data.binaryData.data;
    return misReportBufferArray;
  } catch (err) {
    console.error('Error fetching Blank MIS Report Form:', err);
    throw err;
  }
};


export default {
  getCrashLocationReport,
  getCrashDOTReport,
  getCSALocationReport,
  getCSADOTReport,
  getCSABasicsDOTScoreGraph,
  getDocumentReport,
  getComplianceReport,
  getAccidentLocationReport,
  getAccidentDOTReport,
  getEmployeesDOTReport,
  getReportOfAllMedCardsByLocation,
  getMisReport,
  getBlankMisReportForm
};